(ns dashboard.components.calendar
  (:require
   [clojure.string :refer [split lower-case]]
   [re-frame.core :as re-frame]
   [breaking-point.core :as bp]
   [headlessui-reagent.core :as ui]
   [dashboard.i18n :refer [tr]]
   [dashboard.subs :as subs]
   [dashboard.utils :refer [listen cs duration-in-hours]]
   ["date-fns" :refer
    [parse format formatISO setMonth setYear startOfMonth endOfMonth startOfWeek
     endOfWeek eachDayOfInterval setISODay getISOWeek addDays isAfter parse
     getDay getMonth getYear Interval isToday getWeek setWeek setHours isSameDay
     getHours getMinutes]]))

(defn calendar-month
  [{:keys [month events]
    :or {month (js/Date.)}}]
  (let [month-start (startOfMonth month)
        first-week-day (startOfWeek month-start)
        month-end (endOfMonth month)
        last-week-day (endOfWeek month-end)
        days (eachDayOfInterval
              (clj->js
               {:start (-> month-start
                           (startOfMonth)
                           (startOfWeek #js {:weekStartsOn 1}))
                :end (-> month-end
                         (endOfMonth)
                         (endOfWeek #js {:weekStartsOn 1}))}))
        rows-count (/ (count days) 7)]
    [:div.shadow.ring-1.ring-black.ring-opacity-5.lg:flex.lg:flex-auto.lg:flex-col
     [:div.grid.grid-cols-7.gap-px.border-b.border-gray-300.bg-gray-200.text-center.text-xs.font-semibold.leading-6.text-gray-700.lg:flex-none
      (doall
       (for [dow (range 1 8)]
         ^{:key (str "month-calendar-dow-" dow)}
         [:div.bg-white.py-2
          [:span
           (format (setISODay (js/Date.) dow) "EEEEE"
                   #js {:locale (listen ::subs/date-fns-locale)})]
          [:span.sr-only.sm:not-sr-only]]))]

     [:div.flex.bg-gray-200.text-xs.leading-6.text-gray-700.lg:flex-auto
      [:div.hidden.w-full.lg:grid.lg:grid-cols-7.lg:gap-px
       {:class (str "lg:grid-rows-" rows-count)}
       (doall
        (for [day days
              :let [is-current-month? (= (getMonth day) month)
                    is-today? (isToday day)
                    day-str (format day "yyyy-MM-dd")
                    today-events (->> events
                                      (filter #(= (:date %) day-str)))]]
          ^{:key (formatISO day)}
          [:div.relative.py-2.px-3
           {:class (cs "flex h-32 flex-col hover:bg-gray-100 focus:z-10"
                       (if is-current-month?
                         "bg-white"
                         "bg-gray-50 text-gray-500"))}
           [:time
            {:dateTime day-str
             :class (cs ""
                        (when is-today? "flex h-6 w-6 items-center justify-center rounded-full bg-primary-600 font-semibold text-white"))}
            (format day "d")]
           [:ol
            (doall
             (for [event today-events]
               ^{:key (gensym)}
               [:li
                [:a.group.flex
                 [:p.flex-auto.truncate.font-medium.group-hover:text-primary-600
                  {:class (cs ""
                              (if (some? (:color (:calendar event)))
                                (str "text-" (:color (:calendar event)) "-800")
                                "text-gray-900"))}
                  (or (:title event) (:symbol (:pattern event)))]
                 [:time.ml-3.hidden.flex-none.text-gray-500.group-hover:text-primary-600.xl:block
                  (or (:startTime event) (:startTime (:pattern event)))]]]))]]))]]]))

(defn small-calendar-month
  [{:keys [selected-date month year events tags on-click]
    :or {month (getMonth (js/Date.))
         year (getYear (js/Date.))
         tags []
         on-click identity}}]
  (let [month (or month (getMonth (js/Date.)))
        year (or year (getYear (js/Date.)))
        m (setMonth (setYear (js/Date.) year) month)
        month-start (startOfMonth m)
        first-week-day (startOfWeek month-start)
        month-end (endOfMonth m)
        last-week-day (endOfWeek month-end)
        days (eachDayOfInterval
              (clj->js
               {:start (->> (setMonth (setYear (js/Date.) year) month)
                            (startOfMonth)
                            (startOfWeek))
                :end (->> (setMonth (setYear (js/Date.) year) month)
                          (endOfMonth)
                          (endOfWeek))}))
        rows-count (/ (count days) 7)]
    [:div
     [:div.mt-6.grid.grid-cols-7.text-xs.leading-6.text-gray-500
      (doall
       (for [dow (range 7)]
         ^{:key (str "calendar-dow-" dow)}
         [:div
          (format (setISODay (js/Date.) dow) "EEEEE"
                  #js {:locale (listen ::subs/date-fns-locale)})]))]

     [:div.isolate.mt-2.grid.grid-cols-7.gap-px.rounded-lg.bg-gray-200.text-sm.shadow.ring-1.ring-gray-200
      (doall
       (for [day days
             :let [is-current-month? (= (getMonth day) month)
                   is-today? (isToday day)
                   is-selected? (isSameDay selected-date day)
                   day-str (format day "yyyy-MM-dd")
                   today-events (->> events
                                     (filter #(= (:date %) day-str)))]]
         ^{:key (formatISO day)}
         [:button.relative.focus:outline-none.focus:ring-2.focus:ring-primary-500
          {:type "button"
           :class (cs "py-1.5 hover:bg-gray-100 focus:z-10"
                      (if is-current-month?
                        "bg-white"
                        "bg-gray-50 text-gray-500"))
           :on-click #(on-click day)}
          (when (not (empty? today-events))
            [:span.bg-gray-400.absolute.left-0.w-full.rounded-full.z-2.ml-1
             {:class "h-1.5 w-1.5"}])
          [:time
           {:dateTime day-str
            :class (cs "mx-auto flex h-7 w-7 items-center justify-center rounded-full"
                       (if is-today?
                         "bg-primary-600 text-white")
                       (if (and is-selected? (not is-today?))
                         "text-primary-500 font-semibold"))}
           (format day "d")]
          [:ol
           (doall
            (for [event today-events]
              ^{:key (gensym)}
              [:li
               [:a.group.flex
                [:p.flex-auto.truncate.font-medium.text-gray-900.group-hover:text-primary-600
                 (or (:title event) (:symbol (:pattern event)))]
                [:time.ml-3.hidden.flex-none.text-gray-500.group-hover:text-primary-600.xl:block
                 (:startTime (:pattern event))]]]))]]))]]))

(defn calendar-week
  [{:keys [month week year events]
    :or {week (getWeek (or month (js/Date.)))
         year (getYear (or month (js/Date.)))}}]
  (let [days (eachDayOfInterval
              (clj->js
               {:start (-> (setWeek (setYear (js/Date.) year) week)
                           (startOfWeek #js {:weekStartsOn 1}))
                :end (-> (setWeek (setYear (js/Date.) year) week)
                         (endOfWeek #js {:weekStartsOn 1}))}))
        start-hour 8
        end-hour   22
        hours (- end-hour start-hour)
        intervals (* hours 12)]
    [:div.h-screen.flex.flex-col
     [:div.flex.flex-auto.flex-col.overflow-auto.bg-white
      [:div.flex.max-w-full.flex-none.flex-col.sm:max-w-none.md:max-w-full
       {:style {:width "165%"}}
       [:div.sticky.top-0.z-10.flex-none.bg-white.shadow.ring-1.ring-black.ring-opacity-5.sm:pr-8
        [:div.grid.grid-cols-7.text-sm.leading-6.text-gray-500.sm:hidden
         (doall
          (for [day days
                :let [is-today? (isToday day)]]
            ^{:key (str "mobile-header-" (format day "yyyy-MM-dd"))}
            [:button.flex.flex-col.items-center.pt-2.pb-3
             {:type "button"
              ;; FIXME: implement daily navigation when on mobile
              :on-click identity}
             (format day "EEEEE")
             [:span.mt-1.flex.h-8.w-8.items-center.justify-center.font-semibold
              {:class (cs ""
                          (if is-today?
                            "rounded-full bg-primary-600 text-white"
                            "text-gray-900"))}
              (format day "d")]]))]
        [:div.-mr-px.hidden.grid-cols-7.divide-x.divide-gray-100.border-r.border-gray-100.text-sm.leading-6.text-gray-500.sm:grid
         [:div.col-end-1.w-14]
         (doall
          (for [day days
                :let [is-today? (isToday day)]]
            ^{:key (str "mobile-header-" (format day "yyyy-MM-dd"))}
            [:div.flex.items-center.justify-center.py-3
             [:span
              (format day "E")]
             [:span.items-center.justify-center.font-semibold
              {:class (cs "ml-1.5"
                          (if is-today?
                            "flex h-6 w-6 rounded-full bg-primary-600 text-white"
                            "text-gray-900"))}
              (format day "d")]]))]]
       [:div.flex.flex-auto
        [:div.sticky.left-0.z-10.w-14.flex-none.bg-white.ring-1.ring-gray-100]
        [:div.grid.flex-auto.grid-cols-1.grid-rows-1

         ;; Horizontal Lines
         [:div.col-start-1.col-end-2.row-start-1.grid.divide-y.divide-gray-100
          {:style {:gridTemplateRows (str "repeat(" hours ", minmax(3.5rem, 1fr))")}}
          [:div.row-end-1.h-7]
          (doall
           (for [hour (range start-hour end-hour)
                 :let [datetime (setHours (js/Date.) hour)]]
             ^{:key (str "hours-" hour)}
             [:div
              [:div.sticky.left-0.z-20.-ml-14.w-14.pr-2.text-right.text-xs.leading-5.text-gray-400.whitespace-nowrap
               {:class "-mt-2.5"}
               (format datetime "h:00 a")]]))]

         ;; Vertical Lines
         [:div.col-start-1.col-end-2.row-start-1.hidden.grid-cols-7.grid-rows-1.divide-x.divide-gray-100.sm:grid.sm:grid-cols-7
          [:div.col-start-1.row-span-full]
          [:div.col-start-2.row-span-full]
          [:div.col-start-3.row-span-full]
          [:div.col-start-4.row-span-full]
          [:div.col-start-5.row-span-full]
          [:div.col-start-6.row-span-full]
          [:div.col-start-7.row-span-full]
          [:div.col-start-8.row-span-full.w-8]]

         ;; Events
         [:ol.col-start-1.col-end-2.row-start-1.grid.grid-cols-1.sm:grid-cols-7.sm:pr-8
          {:style {:gridTemplateRows (str "1.75rem repeat(" intervals ", minmax(0, 1fr)) auto")}}
          (doall
           (for [[idx day] (map-indexed vector days)
                 :let [day-str (format day "yyyy-MM-dd")
                       today-events (->> events
                                         (filter #(= (:date %) day-str)))]]
             (doall
              (for [event today-events
                    :let [start-str (or (:startTime event) (:startTime (:pattern event)))
                          hours (if (some? start-str)
                                  (getHours (parse start-str "HH:mm" (js/Date.)))
                                  0)
                          minutes (if (some? start-str)
                                    (getMinutes (parse start-str "HH:mm" (js/Date.)))
                                    0)
                          start-in-hours (+ hours (/ minutes 60))
                          end-str (or (:endTime event) (:endTime (:pattern event)))
                          duration (duration-in-hours
                                    {:start (:start event)
                                     :end (:end event)})
                          grid-start (+ (* 12 (- start-in-hours start-hour)) 1)
                          grid-span (* 12 duration)
                          color (or (:color (:calendar event)) (:color (:pattern event)))]]
                ^{:key (str "weekly-event-" (:id event))}
                [:li.relative.mt-px.flex
                 {:style {:gridRow (str grid-start " / span " grid-span)}}
                 [:a.group.absolute.inset-1.flex.flex-col.overflow-y-auto.rounded-lg.p-2.text-xs.leading-5
                  {:class (cs ""
                              (if (some? color)
                                (str "bg-" color "-50 hover:bg-" color "-100")
                                "bg-gray-50 hover:bg-gray-100"))}
                  [:p.order-1.font-semibold
                   {:class (cs ""
                               (if (some? color)
                                 (str "text-" color "-800")
                                 "text-gray-900"))}
                   (or (:title event) (:symbol (:pattern event)))]
                  [:p
                   {:class (cs ""
                               (if (some? color)
                                 (str "text-" color "-500" " group-hover:text-" color "-700")
                                 "text-gray-500 group-hover:text-gray-700"))}
                   start-str]]]))))]]]]]]))

(defn calendar-day
  [{:keys [day events]
    :or {day (js/Date.)}}]
  (let [start-hour 8
        end-hour   22
        hours (- end-hour start-hour)
        intervals (* hours 12)]
    [:div.flex.flex-auto.overflow-hidden.bg-white
     [:div.flex.flex-auto.flex-col.overflow-auto
      [:div.sticky.top-0.z-10.grid.flex-none.grid-cols-7.bg-white.text-xs.text-gray-500.shadow.ring-1.ring-black.ring-opacity-5.md:hidden
       [:button.flex.flex-col.items-center.pt-3
        {:class "pb-1.5"
         :type "button"}]]
      [:div.flex.w-full.flex-auto
       [:div.w-14.flex-none.bg-white.ring-1.ring-gray-100]
       [:div.grid.flex-auto.grid-cols-1.grid-rows-1

        ;; Horizontal Lines
        [:div.col-start-1.col-end-2.row-start-1.grid.divide-y.divide-gray-100
         {:style {:gridTemplateRows (str "repeat(" hours ", minmax(3.5rem, 1fr))")}}
         [:div.row-end-1.h-7]
         (doall
          (for [hour (range start-hour end-hour)
                :let [datetime (setHours (js/Date.) hour)]]
            ^{:key (str "hours-" hour)}
            [:div
             [:div.sticky.left-0.-mt-2.5.-ml-14.w-14.pr-2.text-right.text-xs.leading-5.text-gray-400.whitespace-nowrap
              (format datetime "h:00 a")]]))]

        ;; Events
        [:ol.col-start-1.col-end-2.row-start-1.grid.grid-cols-1
         {:style {:grid-template-rows (str "1.75rem repeat(" intervals ", minmax(0, 1fr)) auto")}}
         (doall
          (for [event (->> events
                           (filter #(= (:date %) (format day "yyyy-MM-dd"))))
                :let [start-str (or (:startTime event) (:startTime (:pattern event)))
                      hours (if (some? start-str)
                              (getHours (parse start-str "HH:mm" (js/Date.)))
                              0)
                      minutes (if (some? start-str)
                                (getMinutes (parse start-str "HH:mm" (js/Date.)))
                                0)
                      start-in-hours (+ hours (/ minutes 60))
                      end-str (or (:endTime event) (:endTime (:pattern event)))
                      duration (duration-in-hours
                                {:start (:start event)
                                 :end (:end event)})

                      grid-start (+ (* 12 (- start-in-hours start-hour)) 1)
                      grid-span (* 12 duration)
                      color (or (:color (:calendar event)) (:color (:pattern event)))]]
            ^{:key (str "daily-event-" (:id event))}
            [:li.relative.mt-px.flex
             {:style {:gridRow (str grid-start " / span " grid-span)}}
             [:a.group.absolute.inset-1.flex.flex-col.overflow-y-auto.rounded-lg.p-2.text-xs.leading-5
              {:class (cs ""
                          (if (some? color)
                            (str "bg-" color "-50 hover:bg-" color "-100")
                            "bg-gray-50 hover:bg-gray-100"))}
              [:p.order-1.font-semibold
               {:class (cs ""
                           (if (some? color)
                             (str "text-" color "-800")
                             "text-gray-900"))}
               (or (:title event) (:symbol (:pattern event)))]
              [:p
               {:class (cs ""
                           (if (some? color)
                             (str "text-" color "-500" " group-hover:text-" color "-700")
                             "text-gray-500 group-hover:text-gray-700"))}
               start-str]]]))]]]]]))
